<template>
  <v-dialog v-model="dialogComputed" max-width="1500px" scrollable persistent>
    <v-card>
      <v-card-title class="primary white--text py-2">
        {{ dialogTitle }}
        <v-spacer />
        <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
      </v-card-title>
      <v-card-text>
        <validation-observer v-if="dialogComputed" ref="observer" v-slot="{ invalid }">
          <v-container fluid class="px-0">
            <v-row>
              <v-col>
                <TextFieldRef v-if="!isCopy" :label="$t('ELE0137')" v-model="inspIndexNo" />
                <Checkbox v-else-if="isCopy" class="mr-3" v-model="isDataCopy" :label="$t('ELE0541')" />
              </v-col>
            </v-row>
            <v-card outlined>
              <v-card-text>
                <v-row>
                  <v-col cols="3">
                    <OqcIqcInput v-model="editForm.inspTimingCd" required :disabled="!canEditAll" />
                  </v-col>
                  <v-col cols="3">
                    <ManufacturerInput
                      :label="$t('ELE0057')"
                      v-model="editForm.manufacturerCd"
                      required
                      :disabled="!canEditAll"
                      :validOnly="canEditAll"
                    />
                  </v-col>
                  <v-col cols="3">
                    <InspCat1Input
                      v-model="editForm.inspCat1"
                      validOnly
                      required
                      :disabled="!canEditAll && !canEditNonCopyKey"
                    />
                  </v-col>
                  <v-col cols="3">
                    <InspCat2Input
                      v-model="editForm.inspCat2"
                      @change="onChangeInspCat2"
                      validOnly
                      required
                      :disabled="!canEditAll && !canEditNonKey"
                      :isStartUp="isStartUp"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="3">
                    <CompanyInput v-if="!canEditAll" v-model="editForm.inspCompanyCd" :label="$t('ELE0048')" disabled />
                    <CompanyInput
                      v-else
                      v-model="editForm.inspCompanyCd"
                      :label="$t('ELE0048')"
                      :disabled="isSupplier"
                      validOnly
                      required
                    />
                  </v-col>
                  <v-col cols="3">
                    <TextFieldRef
                      v-if="!canEditAll && !canEditNonCopyKey"
                      :label="$t('ELE0049')"
                      :value="editForm.lotNo"
                    />
                    <TextFieldInput
                      v-else
                      :label="$t('ELE0049')"
                      validationMode="aggressive"
                      :rules="`lotNoFormat:${formatLotNo}`"
                      v-model="editForm.lotNo"
                      required
                    />
                  </v-col>
                  <v-col cols="6">
                    <SupplierPartInput
                      v-model="editForm.partCd"
                      :supplierCd="editForm.manufacturerCd"
                      validOnly
                      required
                      :disabled="!canEditAll"
                      @change="onChangePartCd"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="3">
                    <TextFieldRef
                      v-if="!canEditAll && !canEditNonKey && !canEditNonCopyKey"
                      :label="$t('ELE0050')"
                      :value="editForm.lotQty"
                      right
                    />
                    <TextFieldInput
                      v-else
                      :label="$t('ELE0050')"
                      v-model="editForm.lotQty"
                      validationMode="aggressive"
                      :debounce="500"
                      rules="naturalNumberQty"
                      right
                      required
                    />
                  </v-col>
                  <v-col cols="3">
                    <TextFieldRef v-if="!canEditAll || isMoldManaged" :label="$t('ELE0051')" :value="editForm.moldNo" />
                    <TextFieldInput
                      v-else
                      :label="$t('ELE0051')"
                      v-model="editForm.moldNo"
                      rules="halfWidth"
                      maxLength="20"
                    />
                  </v-col>
                  <v-col cols="3">
                    <TextFieldRef :label="$t('ELE0052')" :value="editForm.mmmmApplNo" />
                  </v-col>
                  <v-col cols="3">
                    <TextFieldRef
                      v-if="!canEditAll && !canEditNonCopyKey"
                      :label="$t('ELE0053')"
                      :value="editForm.matlSpec"
                    />
                    <TextFieldInput
                      v-else
                      :label="$t('ELE0053')"
                      v-model="editForm.matlSpec"
                      rules="halfWidth"
                      maxLength="50"
                      :required="isReqMatlSpec"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <TextAreaRef
                      v-if="!canEditAll && !canEditNonKey && !canEditNonCopyKey"
                      :label="$t('ELE0054')"
                      :value="editForm.remarks"
                      rows="1"
                    />
                    <TextAreaInput v-else :label="$t('ELE0054')" v-model="editForm.remarks" rows="1" maxLength="1000" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <v-card outlined>
              <v-card-title>
                {{ $t('ELE0142') }}
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col>
                    <InspFilteringInput
                      :label="inspFilteringLabel"
                      v-model="partInfo.inspFilteringGroupCd"
                      validOnly
                      disabled
                    />
                  </v-col>
                  <v-col>
                    <TextFieldRef :label="$t('ELE0115')" v-model="partInfo.partInspTimingName" />
                  </v-col>
                  <v-col>
                    <TextFieldRef :label="$t('ELE0144')" v-model="partInfo.inspBasicQty" right />
                  </v-col>
                  <v-col>
                    <AqlRateInput v-model="partInfo.aqlRateCd" validOnly disabled />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col v-if="isEdit || isCopy" class="d-flex justify-end">
                    <BtnDelete :disabled="!canDelete" @click="onClickDelete" />
                    <BtnSave class="mx-4" @click="onClickSave" :disabled="invalid" />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-container>
        </validation-observer>
      </v-card-text>
      <ConfirmDialog
        :dialog="isConfirmDialog"
        :confirmWord="$t('MSG0013')"
        :btnLabel="$t('ELE0036')"
        @cancel="onCancel"
        @proceed="onProceed"
      />
      <ConfirmDialog
        :dialog="inspIndexDuplicationDialog"
        :confirmWord="$t('MSG0055')"
        :btnLabel="$t('ELE0019')"
        @cancel="onCancel"
        @proceed="saveProcess"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { ModalMode, InspStatusCd, OqcIqcCd, SystemParameterKey, InspectionIndex4M } from '@/enum'
import { ValidationObserver } from 'vee-validate'
import { API } from '@/plugins/api'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import APIUtils from '@/mixin/api-utils.js'
import CheckUtil from '@/mixin/check-utils.js'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import Btn from '@/components/atoms/base/button/Btn'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnCopy from '@/components/atoms/button/BtnCopy'
import BtnDelete from '@/components/atoms/button/BtnDelete'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import TextAreaRef from '@/components/atoms/base/text-field/TextAreaRef'
import OqcIqcInput from '@/components/atoms/select/OqcIqcInput'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import InspCat1Input from '@/components/atoms/select/InspCat1Input'
import InspCat2Input from '@/components/atoms/select/InspCat2Input'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import ConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'
import AqlRateInput from '@/components/atoms/select/AqlRateInput'
import InspFilteringInput from '@/components/atoms/select/InspFilteringInput'

export default {
  name: 'InspIndexDialog',

  mixins: [Message, Loading, APIUtils, CheckUtil],

  components: {
    ValidationObserver,
    Checkbox,
    Btn,
    BtnSave,
    BtnCopy,
    BtnDelete,
    TextFieldInput,
    TextFieldRef,
    TextAreaInput,
    TextAreaRef,
    OqcIqcInput,
    ManufacturerInput,
    InspCat1Input,
    InspCat2Input,
    CompanyInput,
    SupplierPartInput,
    ConfirmDialog,
    AqlRateInput,
    InspFilteringInput,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    inspIndexNo: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      settings: [
        {
          mode: ModalMode.INQUIRY,
          title: this.$t('ELE0018'),
          url: '/inspIndex/inspIndexes/{id}',
        },
        {
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/inspIndex/inspIndexes/{id}/update',
        },
        {
          mode: ModalMode.COPY,
          title: this.$t('ELE0251'),
          url: '/inspIndex/inspIndexes/{id}/copy',
        },
      ],
      localMode: this.mode,
      isConfirmDialog: false,
      inspIndexDuplicationDialog: false,
      isDataCopy: false,
      moldManaged: false,
      isInspIndex4M: false,
      formatLotNo: '',
      lotNoFormatRules: {
        normal: '',
        startup: '',
      },
      isReqMatlSpec: false,
      companyCatCd: '',
      companyType: '',
      initialEditForm: {}, // 初期値を保存するオブジェクト
      editForm: {
        inspTimingCd: '',
        manufacturerCd: '',
        lotNo: '',
        partCd: '',
        inspCat1: '',
        inspCat2: '',
        lotQty: '',
        inspCompanyCd: this.$store.getters.companyCd,
        moldNo: '',
        mmmmApplNo: '',
        matlSpec: '',
        remarks: '',
        versionNo: null,
      },
      partInfo: {
        inspFilteringGroupCd: '',
        partInspTimingName: '',
        inspBasicQty: '',
        aqlRateCd: '',
        partRev: '',
      },
    }
  },
  computed: {
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    dialogTitle: {
      get() {
        let result = this.setting.title
        if (this.isCopy) {
          result = this.$t('ELE0515', { p0: this.inspIndexNo })
        }
        return result
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.localMode)
      },
    },
    isSupplier() {
      return this.$store.getters.isSupplier
    },
    isMoldManaged: {
      get() {
        return this.moldManaged
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
    isCopy: {
      get() {
        return this.setting.mode === ModalMode.COPY
      },
    },
    canDelete: {
      get() {
        return this.setting.mode === ModalMode.EDIT && this.editForm.inspStatusCd == InspStatusCd.WAITING_FOR
      },
    },
    canEditAll: {
      get() {
        // 全項目編集できる条件
        // 1. 編集モードで、検査中以外
        // 2. コピーモードで、検査データコピーをしない場合
        let result = false
        if (this.setting.mode === ModalMode.EDIT) {
          result = this.editForm.inspStatusCd != InspStatusCd.IN_PROGRESS
        } else if (this.setting.mode === ModalMode.COPY) {
          result = this.isDataCopy == false
        }
        return result
      },
    },
    canEditNonKey: {
      get() {
        // 検査中でも、key以外の一部項目のみ編集を許可する条件
        let result = false
        if (this.setting.mode === ModalMode.EDIT) {
          result = true
        }
        return result
      },
    },
    canEditNonCopyKey: {
      get() {
        // 検査データコピー時、key以外の一部項目のみ編集を許可する条件
        let result = false
        if (this.setting.mode === ModalMode.COPY && this.isDataCopy == true) {
          result = true
        }
        return result
      },
    },
    inspFilteringLabel: {
      get() {
        let result = ''
        if (this.$store.getters.canOQCOnly === true) {
          result = this.$t('ELE0289')
        } else {
          result = this.$t('ELE0290')
        }
        return result
      },
    },
    isStartUp: {
      // inspCat2の選択肢制限
      get() {
        let result = null
        if (this.setting.mode === ModalMode.EDIT && this.editForm.inspStatusCd == InspStatusCd.IN_PROGRESS) {
          result = this.editForm['m_inspCat2'].isStartUp
        }
        return result
      },
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
      this.$emit('close') // 親コンポーネントにcloseイベントを通知
    },
    updateMode(newMode) {
      this.localMode = newMode
    },
    resetDisabledFields() {
      // 入力不可フィールド一覧
      const fieldsToReset = ['inspTimingCd', 'manufacturerCd', 'inspCompanyCd', 'partCd', 'moldNo']
      if (this.canEditNonCopyKey) {
        fieldsToReset.forEach((field) => {
          // 入力不可のフィールドのみ初期値にリセット
          this.editForm[field] = this.initialEditForm[field]
        })
      }
    },
    async onClickSave() {
      this.startLoading()
      try {
        const params = {
          inspTimingCd: this.editForm.inspTimingCd,
          manufacturerCd: this.editForm.manufacturerCd,
          lotNo: this.editForm.lotNo,
          partCd: this.editForm.partCd,
          inspCompanyCd: this.editForm.inspCompany,
        }
        const element = await API.get('/inspIndex/lot/complete', params)
        if (element) {
          this.inspIndexDuplicationDialog = true
        } else {
          this.saveProcess()
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    async saveProcess() {
      this.startLoading()
      try {
        if (!(await this.$refs.observer.validate())) {
          return
        }
        const body = {
          userCompanyCd: this.$store.getters.companyCd,
          dataCopyFlag: this.isDataCopy,
          prevInspIndexNo: this.inspIndexNo,
          items: {
            inspTimingCd: this.editForm.inspTimingCd,
            manufacturerCd: this.editForm.manufacturerCd,
            lotNo: this.editForm.lotNo,
            partCd: this.editForm.partCd,
            inspCat1: this.editForm.inspCat1,
            inspCat2: this.editForm.inspCat2,
            lotQty: this.editForm.lotQty,
            inspCompanyCd: this.editForm.inspCompanyCd,
            moldNo: this.editForm.moldNo,
            mmmmApplNo: this.editForm.mmmmApplNo,
            matlSpec: this.editForm.matlSpec,
            remarks: this.editForm.remarks,
            versionNo: this.editForm.versionNo,
          },
        }
        const url = this.setting.url.replace('{id}', this.inspIndexNo)
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0118') }))
        this.close()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    async onInitSearch() {
      try {
        this.startLoading()
        // モーダルOpen時の初期値取得
        const params = {
          userCompanyCd: this.$store.getters.companyCd,
        }
        const url = '/inspIndex/inspIndexes/' + this.inspIndexNo
        const element = await API.get(url, params)
        this.editForm = element // 取得した初期値をモーダル内変数にセット
        this.initialEditForm = JSON.parse(JSON.stringify(element)) // 初期値を別変数に保存
        this.partInfo = {
          partInspTimingName: element['m_partInspTiming'].partInspTimingName,
          inspBasicQty: element['m_part'].inspBasicQty,
          aqlRateCd: element['m_partAqlRate'].aqlRateCd,
          partRev: element['m_part'].partRev,
        }
        this.partInfo.inspFilteringGroupCd = element['m_partInspFiltering'].inspFilteringGroupCd
        this.editForm.lotQty = element['t_lot'].lotQty
        // コピーモーダルで開いた時、mmmmApplNoがnullでない場合はInspectionIndex4M.NOT_STARTED_YETに
        if (this.isCopy && element['mmmmApplNo']) {
          this.editForm.mmmmApplNo = InspectionIndex4M.NOT_STARTED_YET
        }
        this.moldManaged = element['m_part'].isManagedMold
        if (this.moldManaged) {
          this.editForm.moldNo = element['m_part'].moldNo
        }
        // EDITの場合、検査インデックスの検査ステータスに応じてモーダルのモードを設定
        if (this.setting.mode === ModalMode.EDIT) {
          if (
            element.inspStatusCd !== InspStatusCd.COMPLETE &&
            this.isInspectableCompany(element.inspCompanyCd, element['m_company'].companyCatCd)
          ) {
            this.localMode = ModalMode.EDIT
          } else {
            this.localMode = ModalMode.INQUIRY
          }
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    onCancel() {
      this.isConfirmDialog = false
      this.inspIndexDuplicationDialog = false
    },
    onClickDelete() {
      this.isConfirmDialog = true
    },
    async onProceed() {
      this.startLoading()
      try {
        this.isConfirmDialog = false

        const url = '/inspIndex/inspIndexes/' + this.inspIndexNo + '/delete'
        const body = {
          versionNo: this.editForm.versionNo,
        }
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0014'))
        this.close()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0004', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    async onChangePartCd() {
      try {
        this.startLoading()
        if (!this.editForm.partCd) {
          return
        }
        const params = {}
        if (this.$store.getters.canOQCOnly === true) {
          params.inspTimingCd = OqcIqcCd.OQC
        } else params.inspTimingCd = OqcIqcCd.IQC
        const url = `/master/parts/${this.editForm.partCd}/info`
        const element = await API.get(url, params)
        this.partInfo = {
          partInspTimingName: element.partInspTimingName,
          inspBasicQty: element.inspBasicQty,
          aqlRateCd: element.aqlRateCd,
          inspFilteringGroupCd: element.inspFilteringGroupCd,
        }
        if (
          (element.isManagedMold && !this.moldManaged) ||
          (!element.isManagedMold && this.moldManaged) ||
          element.isManagedMold
        ) {
          this.editForm.moldNo = element.moldNo
        }
        this.moldManaged = element.isManagedMold
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0059') })))
      } finally {
        this.stopLoading()
      }
    },
    async onChangeInspCat2(inspCatItem) {
      if (inspCatItem) {
        this.formatLotNo = inspCatItem.isStartUp ? this.lotNoFormatRules.startup : this.lotNoFormatRules.normal
        this.isInspIndex4M = inspCatItem.isMmmm
        this.editForm.mmmmApplNo = this.isInspIndex4M ? InspectionIndex4M.NOT_STARTED_YET : ''
      }
    },
    async setFormatLotNo() {
      try {
        this.startLoading()
        const param = await API.get(`/master/systemParameter/${SystemParameterKey.FORMAT_LOT_NO}`)
        this.formatLotNo = param.value
      } catch (error) {
        this.formatLotNo = ''
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      } finally {
        this.stopLoading()
      }
    },
    async setMatlSpecReq() {
      let result = ''
      try {
        this.startLoading()
        const param = await API.get(`/master/systemParameter/${SystemParameterKey.MATL_SPEC_REQ}`)
        result = param.value
      } catch (error) {
        result = ''
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      } finally {
        if (result == 'TRUE') {
          this.isReqMatlSpec = true
        }
        this.stopLoading()
      }
    },
  },

  watch: {
    isDataCopy(check) {
      if (check) {
        // 入力不可のフィールドのみ初期値にリセット
        this.resetDisabledFields()
      }
    },
    mode(newMode) {
      this.localMode = newMode
    },
  },

  async mounted() {
    await this.setFormatLotNo()
    await this.setMatlSpecReq()
    if (this.inspIndexNo) {
      this.onInitSearch()
    }
  },
}
</script>
